import {
  DEFAULT_THEME,
  PLAYER_THEME_IDS,
  isDarkPlayerTheme,
  type PlayerThemeId,
} from './theme.config';
import { applyPlayerLayout } from './playerLayout';

export const PLAYER_THEMES = PLAYER_THEME_IDS;
export type { PlayerThemeId };

export const DEFAULT_PLAYER_THEME: PlayerThemeId = DEFAULT_THEME;

export function normalizePlayerTheme(raw?: string | null): PlayerThemeId {
  if (raw && (PLAYER_THEMES as readonly string[]).includes(raw)) {
    return raw as PlayerThemeId;
  }
  return DEFAULT_PLAYER_THEME;
}

export function usesLogoTheme(theme: PlayerThemeId): boolean {
  return theme === 'tottalo';
}

/**
 * Every palette paints its chrome (header/menu/footer) dark, so "dark theme"
 * here means the *content* surfaces are dark too — cards, lists, match rows.
 * CSS keys off `data-theme-surface` rather than listing both dark palettes in
 * every override selector.
 */
export function playerThemeSurface(theme: PlayerThemeId): 'light' | 'dark' {
  return isDarkPlayerTheme(theme) ? 'dark' : 'light';
}

/** The palette last shown on this device; public/index.html reads it too. */
const DEVICE_THEME_KEY = 'tottalo.theme';

/**
 * The theme the login screen wears: the one the last player on this device
 * had, so logging out (or coming back later) does not flip the site to
 * another palette. A device nobody has logged in on gets the default.
 */
export function readDeviceTheme(): PlayerThemeId {
  try {
    return normalizePlayerTheme(localStorage.getItem(DEVICE_THEME_KEY));
  } catch {
    return DEFAULT_PLAYER_THEME;
  }
}

export function applyPlayerTheme(theme?: string | null): PlayerThemeId {
  const normalized = normalizePlayerTheme(theme);
  try {
    localStorage.setItem(DEVICE_THEME_KEY, normalized);
  } catch {
    // Private mode: the login screen falls back to the default.
  }
  document.documentElement.dataset.playerTheme = normalized;
  document.documentElement.dataset.themeSurface =
    playerThemeSurface(normalized);
  // The modern layout forces dark surfaces and is off for tottalo, so it is
  // re-resolved whenever the palette changes.
  applyPlayerLayout();
  return normalized;
}

export function readAppliedPlayerTheme(): PlayerThemeId {
  return normalizePlayerTheme(document.documentElement.dataset.playerTheme);
}
